import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { MapPin, Navigation, AlertCircle, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const EmbeddedTrackingMap = ({ ride }) => {
  const { user } = useContext(AuthContext);
  const [isTracking, setIsTracking] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [mapMode, setMapMode] = useState('overview'); // overview, pickup, dropoff
  const watchIdRef = useRef(null);
  const isDriver = user?.id === ride.assigned_to;
  const isRider = user?.id === ride.rider_id;

  const updateLocationOnServer = useCallback(async (latitude, longitude) => {
    try {
      await axios.post(`${API}/tracking/update-location`, {
        ride_id: ride.id,
        latitude,
        longitude
      });
    } catch (error) {
      console.error('Failed to update location:', error);
    }
  }, [ride.id]);

  useEffect(() => {
    if (isDriver && isTracking) {
      if (!navigator.geolocation) {
        setLocationError('Geolocation not supported');
        toast.error('Your browser doesn\'t support location tracking');
        return;
      }

      watchIdRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setDriverLocation({ latitude, longitude });
          updateLocationOnServer(latitude, longitude);
          setLocationError('');
        },
        (error) => {
          setLocationError('Failed to get location: ' + error.message);
          toast.error('Location access denied. Please enable location services.');
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 60000
        }
      );
    } else if (isRider) {
      const pollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/tracking/${ride.id}`);
          if (response.data.tracking) {
            setDriverLocation(response.data.tracking);
          }
        } catch (error) {
          console.error('Failed to fetch driver location:', error);
        }
      }, 5000);

      return () => clearInterval(pollInterval);
    }

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [isTracking, isDriver, isRider, ride.id, updateLocationOnServer]);

  const getMapUrl = (mode) => {
    const pickup = encodeURIComponent(ride.pickup_location);
    const dropoff = encodeURIComponent(ride.dropoff_location);
    
    switch (mode) {
      case 'pickup':
        if (driverLocation) {
          return `https://www.google.com/maps/embed/v1/directions?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&origin=${driverLocation.latitude},${driverLocation.longitude}&destination=${pickup}&mode=driving`;
        }
        return `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${pickup}`;
      
      case 'dropoff':
        if (driverLocation) {
          return `https://www.google.com/maps/embed/v1/directions?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&origin=${driverLocation.latitude},${driverLocation.longitude}&destination=${dropoff}&mode=driving`;
        }
        return `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${dropoff}`;
      
      default: // overview
        return `https://www.google.com/maps/embed/v1/directions?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&origin=${pickup}&destination=${dropoff}&mode=driving`;
    }
  };

  const getCurrentMapTitle = () => {
    switch (mapMode) {
      case 'pickup':
        return 'Navigation to Pickup';
      case 'dropoff':
        return 'Navigation to Dropoff';
      default:
        return 'Route Overview';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            {getCurrentMapTitle()}
          </span>
          {isDriver && (
            <Button
              size="sm"
              onClick={() => setIsTracking(!isTracking)}
              className={isTracking ? 'bg-green-600' : 'bg-blue-600'}
            >
              <Navigation className="w-4 h-4 mr-1" />
              {isTracking ? 'Tracking On' : 'Start Tracking'}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {locationError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <span className="text-sm font-semibold text-red-800">Location Error</span>
              </div>
              <p className="text-sm text-red-800">{locationError}</p>
            </div>
          )}

          {/* Map Navigation Buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              size="sm"
              variant={mapMode === 'overview' ? 'default' : 'outline'}
              onClick={() => setMapMode('overview')}
            >
              Overview
            </Button>
            <Button
              size="sm"
              variant={mapMode === 'pickup' ? 'default' : 'outline'}
              onClick={() => setMapMode('pickup')}
              className="bg-green-50 text-green-700 border-green-200"
            >
              To Pickup
            </Button>
            {(isDriver && ride.status === 'picked_up') && (
              <Button
                size="sm"
                variant={mapMode === 'dropoff' ? 'default' : 'outline'}
                onClick={() => setMapMode('dropoff')}
                className="bg-red-50 text-red-700 border-red-200"
              >
                To Dropoff
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          {/* Driver Location Display */}
          {driverLocation && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-semibold text-blue-900 mb-2">
                {isDriver ? 'Your Location' : 'Driver Location'}
              </h4>
              <div className="text-sm text-blue-800 space-y-1">
                <p>📍 Lat: {driverLocation.latitude.toFixed(6)}</p>
                <p>📍 Lng: {driverLocation.longitude.toFixed(6)}</p>
                {driverLocation.updated_at && (
                  <p>🕐 Updated: {new Date(driverLocation.updated_at).toLocaleTimeString()}</p>
                )}
              </div>
            </div>
          )}

          {/* Embedded Google Maps */}
          <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
            <iframe
              width="100%"
              height="400"
              style={{ border: 0 }}
              src={getMapUrl(mapMode)}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={getCurrentMapTitle()}
            ></iframe>
          </div>

          {/* Route Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <h4 className="font-semibold text-green-900 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                Pickup
              </h4>
              <p className="text-sm text-green-800 mt-1">{ride.pickup_location}</p>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <h4 className="font-semibold text-red-900 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                Dropoff
              </h4>
              <p className="text-sm text-red-800 mt-1">{ride.dropoff_location}</p>
            </div>
          </div>

          {/* Status Display */}
          {isDriver && isTracking && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>✅ Live Tracking Active</strong>
              <p className="mt-1">
                Your location updates every 5 seconds. Use the map above for turn-by-turn navigation.
              </p>
            </div>
          )}

          {isRider && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
              <strong>🚗 Track Your Driver</strong>
              <p className="mt-1">
                {driverLocation 
                  ? 'See your driver\'s live location above and watch their progress!' 
                  : 'Waiting for driver to start tracking...'}
              </p>
            </div>
          )}

          {/* Legend */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Pickup</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>Dropoff</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>Live Updates</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmbeddedTrackingMap;